import { Badge, Tooltip } from "antd";
import { NoteType, getNotesAndBadge } from "../../domain/notes.domain";
import styles from "./badge.module.scss";

// Imported notes appear one morning, written by nobody the clinician knows, and cannot
// be edited, deleted or transferred. This answers "what is this and why is it locked"
// at the point of confusion. `focus` is in the trigger list on purpose: antd defaults
// to hover only, which is unreachable by keyboard and absent on touch.
const EHR_IMPORTED_HINT =
  "Imported from your EHR during the nightly patient sync so past visits inform new notes. Read-only.";

type Props = {
  note_type?: number | null | undefined;
  internal?: boolean;
  note_type_info?: {
    id: number;
    name: string;
  };
};

export default function NoteTypeBadge({ internal = false, note_type, note_type_info }: Props) {
  const componentName = "badge";

  // Checked FIRST, and against both inputs, because the note_type_info branch below
  // wins whenever the API supplies a name — and it always does. Keying only off
  // note_type meant this chip rendered through that branch instead: right label once
  // the serializer was fixed, but SOAP's colours (id > 13 collapses to 0) and no
  // tooltip. Provenance and the read-only hint are the whole point of the chip, so the
  // imported case owns its own branch rather than depending on which field arrived.
  const isImported = note_type === NoteType.EHR_PRIOR_NOTE || note_type_info?.id === NoteType.EHR_PRIOR_NOTE;

  if (isImported) {
    const { name, badgeStyle } = getNotesAndBadge(NoteType.EHR_PRIOR_NOTE);
    return (
      <Tooltip title={EHR_IMPORTED_HINT} trigger={["hover", "focus"]}>
        {/* tabIndex makes the badge focusable so the tooltip is reachable without a
            pointer; role/aria-label give screen readers the same information. */}
        <span tabIndex={0} role="note" aria-label={EHR_IMPORTED_HINT}>
          <Badge
            count={name}
            showZero
            className={`${styles[`${componentName}__badgestyle`]} ${badgeStyle} ${internal ? styles.internal : ""}`}
          />
        </span>
      </Tooltip>
    );
  }

  if (note_type_info?.name) {
    const { badgeStyle } = getNotesAndBadge(note_type_info.id > 13 ? 0 : note_type_info.id);
    return (
      <Badge
        count={note_type_info.name}
        showZero
        className={`${styles[`${componentName}__badgestyle`]} ${badgeStyle} ${internal ? styles.internal : ""}`}
      />
    );
  }

  if (note_type !== null && note_type !== undefined) {
    const { name, badgeStyle } = getNotesAndBadge(note_type);
    const badge = (
      <Badge
        count={name}
        showZero
        className={`${styles[`${componentName}__badgestyle`]} ${badgeStyle}  ${internal ? styles.internal : ""}`}
      />
    );

    return badge;
  }
  return null;
}
